import { useEffect, useMemo, useRef, useState } from 'react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { api } from '../../lib/api';
import { lireContexteNav } from '../../lib/mini-app-contexte';
import { lirePrenomPassation } from './fluence-prenom-passation';
import { DUREE_MINIMALE_SECONDES, NIVEAUX_FLUENCE as NIVEAUX, estMotCompte } from './fluence-reperes';

interface Mot { i: number; w: string }
interface Question { id: string; position: number; enonce: string; type: 'qcm' | 'ouverte'; options: string[] | null }
interface Support {
  id: string; titre: string; levelCode: string; genre: string | null;
  nombreMots: number; motsComptes: number; mclmExclu: boolean; auteur: string | null;
  paragraphes: string[]; mots: Mot[]; questions: Question[];
}
interface Resultat {
  mclm: number; seuilMin: number | null; seuilMax: number | null;
  position: 'sous' | 'dans' | 'au-dessus' | null; referenceSeuil: string | null;
}

interface Verdict { questionId: string; correcte: boolean; corrigee: boolean }
interface Fiche { id: string; titre: string; nombreMots: number; motsComptes: number; auteur: string | null }

type Etape = 'choisir' | 'preparer' | 'lire' | 'arreter' | 'lecture-enregistree' | 'repondre' | 'verifier' | 'fini';


const MOT_PAR_DEFAUT: Mot[] = [];

function mmss(s: number) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export default function FluenceEleve() {
  const params = new URLSearchParams(window.location.search);
  const evaluationId = params.get('evaluation') ?? '';
  const supportId = params.get('support') ?? '';
  const prenom = lirePrenomPassation(evaluationId);
  const { contexte, retourHref, retourLabel } = lireContexteNav(params);

  /* Sans évaluation, on est en ENTRAÎNEMENT : l'enfant s'exerce seul et rien
     n'est enregistré. L'évaluation se passe en classe, l'enseignant écoute et
     relève les erreurs — compter les essais faits seul fausserait la mesure. */
  const entrainement = evaluationId === '';

  const [support, setSupport] = useState<Support | null>(null);
  const [niveau, setNiveau] = useState<(typeof NIVEAUX)[number]>('CE1');
  const [fiches, setFiches] = useState<Fiche[]>([]);
  const [choixId, setChoixId] = useState(supportId);
  const [verdicts, setVerdicts] = useState<Verdict[]>([]);
  const [etape, setEtape] = useState<Etape>(entrainement && !supportId ? 'choisir' : 'preparer');
  const [secondes, setSecondes] = useState(0);
  const [erreurs, setErreurs] = useState<Set<number>>(new Set());
  const [dernierMot, setDernierMot] = useState<number | null>(null);
  const [qIndex, setQIndex] = useState(0);
  const [reponses, setReponses] = useState<Record<string, string | null>>({});
  /* Les codes du BO, un par question : « 1 » reussi, « A » reussi avec de
     l'aide, « 9 » pas reussi. C'est l'enseignant qui note — l'enfant repond a
     l'oral, l'adulte ecoute. */
  const [scores, setScores] = useState<Record<string, '1' | '9' | 'A'>>({});
  const [noterQuestion, setNoterQuestion] = useState(false);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [erreurEcran, setErreurEcran] = useState<string | null>(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const chrono = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!choixId) {
      if (!entrainement) setErreurEcran("Aucun texte n'a été choisi.");
      return;
    }
    void (api as never as { miniApps: { fluence: { supports: { detail: { query: (i: unknown) => Promise<Support> } } } } })
      .miniApps.fluence.supports.detail.query({ supportId: choixId })
      .then(setSupport)
      .catch(() => setErreurEcran("Ce texte n'a pas pu être chargé."));
  }, [choixId, entrainement]);

  useEffect(() => {
    if (etape !== 'choisir') return;
    void (api as never as { miniApps: { fluence: { supports: { list: { query: (i: unknown) => Promise<Fiche[]> } } } } })
      .miniApps.fluence.supports.list.query({ levelCode: niveau })
      .then(setFiches)
      .catch(() => setFiches([]));
  }, [niveau, etape]);

  useEffect(() => () => { if (chrono.current) clearInterval(chrono.current); }, []);

  const mots = support?.mots ?? MOT_PAR_DEFAUT;
  const questions = useMemo(() => support?.questions ?? [], [support]);

  /* Le texte se rend EN PARAGRAPHES. La liste `mots` est plate, mais elle
     s'aligne exactement sur `paragraphes` — vérifié sur les 189 textes du
     corpus, mot pour mot. Aplatir un récit de 752 mots en 9 paragraphes en un
     seul bloc fait perdre sa ligne à l'enfant qui lit à voix haute, et à
     l'adulte qui suit pour relever les erreurs.
     L'index reste GLOBAL : les erreurs et le dernier mot lu s'y réfèrent. */
  const blocs = useMemo(() => {
    const paras = support?.paragraphes ?? [];
    const sortie: Mot[][] = [];
    let debut = 0;
    for (const para of paras) {
      const n = para.split(/\s+/).filter(Boolean).length;
      if (n === 0) continue;
      sortie.push(mots.slice(debut, debut + n));
      debut += n;
    }
    /* Ceinture : si le découpage ne consomme pas tout — un corpus futur qui
       dériverait — le reste part dans un dernier bloc plutôt que de disparaître
       de l'écran. Un mot non affiché est un mot que l'enfant ne peut pas lire. */
    if (debut < mots.length) sortie.push(mots.slice(debut));
    return sortie.length > 0 ? sortie : [mots];
  }, [support, mots]);

  const demarrer = () => {
    setEtape('lire'); setSecondes(0);
    chrono.current = setInterval(() => setSecondes((s) => s + 1), 1000);
  };
  const arreter = () => {
    if (chrono.current) { clearInterval(chrono.current); chrono.current = null; }
    setEtape('arreter');
  };
  const reprendre = () => {
    setDernierMot(null); setEtape('lire');
    chrono.current = setInterval(() => setSecondes((s) => s + 1), 1000);
  };

  /* Recommencer efface la mesure et le chronometre, PAS le texte choisi : on
     reprend la meme lecture avec le meme enfant. */
  const envoyerComprehension = async (scoresFinaux: Record<string, string>) => {
    setEnvoiEnCours(true); setErreurEcran(null);
    try {
      await (api as never as { miniApps: { fluence: { evaluations: { enregistrerComprehension: { mutate: (i: unknown) => Promise<unknown> } } } } })
        .miniApps.fluence.evaluations.enregistrerComprehension.mutate({
          evaluationId,
          reponses: questions.map((q) => ({ questionId: q.id, reponseDonnee: reponses[q.id] ?? null, scoreBo: scoresFinaux[q.id] ?? null })),
        });
      setEtape('fini');
    } catch {
      setErreurEcran("Les réponses n'ont pas pu être enregistrées. La lecture, elle, est déjà sauvée.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const recommencer = () => {
    if (chrono.current) { clearInterval(chrono.current); chrono.current = null; }
    setSecondes(0); setErreurs(new Set()); setDernierMot(null);
    setQIndex(0); setReponses({}); setScores({}); setNoterQuestion(false); setVerdicts([]); setResultat(null);
    setErreurEcran(null); setEtape('preparer');
  };

  const toucherMot = (i: number) => {
    if (etape === 'lire') {
      setErreurs((prec) => {
        const suite = new Set(prec);
        if (suite.has(i)) suite.delete(i); else suite.add(i);
        return suite;
      });
    } else if (etape === 'arreter') {
      setDernierMot(i);
    }
  };

  /* ON COMPTE LES MOTS, PAS LES JETONS. La ponctuation est affichée — elle
     fait partie du texte — mais un guillemet ne se lit pas à voix haute, et le
     créditer gonflait le MCLM de 5,3 % en médiane, 17 % sur les textes courts
     du CP et du CE1. */
  const motsLus = dernierMot === null
    ? (support?.motsComptes ?? mots.filter((m) => estMotCompte(m.w)).length)
    : mots.slice(0, dernierMot + 1).filter((m) => estMotCompte(m.w)).length;

  const envoyer = async () => {
    setEnvoiEnCours(true); setErreurEcran(null);
    try {
      if (entrainement) {
        const v = await (api as never as { miniApps: { fluence: { entrainement: { corriger: { mutate: (i: unknown) => Promise<Verdict[]> } } } } })
          .miniApps.fluence.entrainement.corriger.mutate({
            supportId: choixId,
            reponses: questions.map((q) => ({ questionId: q.id, reponseDonnee: reponses[q.id] ?? null })),
          });
        setVerdicts(v);
        setEtape('fini');
        return;
      }
      const r = await (api as never as { miniApps: { fluence: { evaluations: { enregistrerResultat: { mutate: (i: unknown) => Promise<Resultat> } } } } })
        .miniApps.fluence.evaluations.enregistrerResultat.mutate({
          evaluationId,
          motsLus,
          /* On ne compte que les erreurs situées AVANT le dernier mot lu : une
             erreur marquée sur un mot jamais atteint n'a pas été commise. */
          erreurs: [...erreurs].filter((i) => i < motsLus).length,
          dureeSecondes: Math.max(1, secondes),
        });
      setResultat(r);
      /* La lecture est SAUVÉE. Les questions viennent après, ou pas du tout —
         s'arrêter ici ne coûte plus rien. */
      setEtape('lecture-enregistree');
    } catch (e) {
      /* EN ENTRAÎNEMENT, RIEN N'EST ENREGISTRÉ — c'est la promesse du mode,
         répétée à l'enfant sur trois écrans. Ce bloc annonçait pourtant que
         « l'enregistrement n'a pas abouti », donc qu'il y avait quelque chose à
         sauver et que c'était perdu. On dit ce qui s'est vraiment passé : la
         correction n'est pas revenue, et il n'y avait rien en jeu. */
      if (entrainement) {
        setErreurEcran("Les réponses n'ont pas pu être vérifiées. Rien n'était enregistré : tu peux réessayer.");
        return;
      }

      /* Le double appui est le cas le plus probable sur une tablette : la
         lecture EST enregistrée, et réessayer ne peut pas aboutir. Dire
         « réessaie » enverrait l'enseignant tourner en rond sur un travail
         déjà fait.
         Deux façons de reconnaître le cas, parce qu'une seule serait fragile :
         le code tRPC — `PRECONDITION_FAILED` n'est rendu que par celui-ci dans
         tout le périmètre fluence — et, à défaut, le texte du domaine. */
      const erreur = e as { message?: string; data?: { code?: string } };
      const dejaLa = erreur?.data?.code === 'PRECONDITION_FAILED'
        || /déjà un résultat/i.test(String(erreur?.message ?? ''));
      /* DÉJÀ ENREGISTRÉE N'EST PAS UNE IMPASSE. L'écran restait sur le relevé
         et disait « vous pouvez fermer » : l'enseignant, devant l'enfant, se
         retrouvait sans aucune sortie alors que le questionnaire l'attendait.
         On l'emmène donc là où la lecture l'a déjà menée — l'étape « lecture
         enregistrée », d'où « Passer aux questions » est ouvert. Le relevé y
         est simplement absent, la mesure ayant été prise à l'envoi précédent. */
      if (dejaLa) {
        setErreurEcran('Cette lecture était déjà enregistrée — la mesure n’a pas bougé. Vous pouvez passer aux questions.');
        setEtape('lecture-enregistree');
        return;
      }
      setErreurEcran("L'enregistrement n'a pas abouti. Rien n'est perdu : réessayez.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  /* DEUX raisons de n'avoir aucun repère, et elles ne se disent pas pareil :
     le NIVEAU n'en publie pas — c'est le CP — ou le TEXTE ne s'y prête pas,
     parce qu'il se lit en vers ou se récite. Les 25 textes concernés sont au
     CE1, CE2, CM1 et CM2, des niveaux qui ont TOUS un repère publié : annoncer
     « pas de repère publié à ce niveau » sur une fable de CE2 était faux. */
  const sansRepere = support?.mclmExclu
    ? 'Ce texte ne se mesure pas en vitesse — il se lit en vers ou se récite'
    : 'Pas de repère publié à ce niveau';

  const carte = 'bg-card border border-border rounded-card shadow-card p-5';
  const gros = 'w-full min-h-[56px] px-5 py-4 rounded-sm font-extrabold text-[17px] border-none cursor-pointer';

  return (
    <>
      <HeaderContent width="md" showBurger backTo={retourHref} backLabel={retourLabel}>
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>{support ? support.titre : 'Lecture'}</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            {contexte ? `${contexte} — ` : ''}
            {prenom ? `${prenom} — ` : ''}
            {/* L'AUTEUR S'AFFICHE, et ce n'est pas une politesse : le droit moral
                de paternité est perpétuel (art. L.121-1 CPI). Le domaine public
                éteint le droit patrimonial, jamais celui-là — montrer une fable
                sans écrire « Jean de La Fontaine » reste fautif. */}
            {/* TROIS ÉTATS, PAS DEUX. « chargement… » restait affiché tant
                qu'aucun texte n'était choisi — c'est-à-dire indéfiniment sur
                l'écran de choix, pendant que la liste était là et cliquable.
                Annoncer un chargement qui n'a pas lieu apprend à l'enfant que
                l'application ment sur son propre état. */}
            {support
              ? `${support.levelCode} · ${support.motsComptes} mots${support.auteur ? ` · ${support.auteur}` : ''}`
              : choixId
                ? 'chargement…'
                : 'choisis un texte pour commencer'}
          </p>
        </div>
      </HeaderContent>

      <main className="max-w-[720px] mx-auto px-5 pb-[max(4rem,env(safe-area-inset-bottom))] pt-4 flex flex-col gap-4">
        {erreurEcran ? (
          <p className="m-0 rounded-sm bg-naia-orange-bg px-4 py-3 text-sm text-foreground">{erreurEcran}</p>
        ) : null}

        {etape === 'choisir' ? (
          <section className={carte}>
            <p className="text-[20px] font-extrabold text-foreground m-0 mb-1">Choisis un texte à lire.</p>
            {/* L'entraînement se DIT, il ne se devine pas : un enfant doit
                savoir quand il est mesuré et quand il s'exerce. */}
            <p className="text-[15px] text-muted-foreground m-0 mb-4 leading-relaxed">
              Ici tu t&apos;entraînes : <b className="text-foreground">rien n&apos;est enregistré</b>. La vraie
              lecture, tu la feras en classe avec ton maître ou ta maîtresse.
            </p>
            <div className="flex gap-2 flex-wrap mb-4">
              {NIVEAUX.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNiveau(n)}
                  className={`min-h-[44px] px-4 rounded-sm font-extrabold text-[15px] cursor-pointer border ${
                    niveau === n ? 'bg-naia-blue text-card border-naia-blue' : 'bg-card text-foreground border-border'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <ul className="list-none m-0 p-0 flex flex-col gap-2">
              {fiches.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => { setChoixId(f.id); setEtape('preparer'); }}
                    className="w-full min-h-[56px] flex items-center gap-3 rounded-sm border border-border bg-card px-4 py-3 text-left cursor-pointer"
                  >
                    <span className="font-bold text-[16px] text-foreground min-w-0 truncate">{f.titre}</span>
                    {f.auteur ? (
                      <span className="shrink-0 text-[13px] italic text-muted-foreground">{f.auteur}</span>
                    ) : null}
                    <span className="ml-auto shrink-0 text-[13px] text-muted-foreground">{f.motsComptes} mots</span>
                  </button>
                </li>
              ))}
              {fiches.length === 0 ? (
                <li className="text-sm text-muted-foreground">Aucun texte pour ce niveau.</li>
              ) : null}
            </ul>
          </section>
        ) : !support ? null : etape === 'preparer' ? (
          <section className={carte}>
            <p className="text-[20px] font-extrabold text-foreground m-0 mb-2">Tu vas lire ce texte à voix haute.</p>
            <p className="text-[15px] text-muted-foreground m-0 mb-4 leading-relaxed">
              {entrainement
                ? "Lis à voix haute, tranquillement. Le chronomètre tourne pour que tu voies ta lecture avancer — rien n'est enregistré."
                : "Un adulte reste à côté de toi : il touche un mot chaque fois que tu te trompes. Lis tranquillement, comme d'habitude. Ce n'est pas une course."}
            </p>
            <div className="rounded-sm bg-naia-info-bg px-4 py-3 mb-4">
              {support.paragraphes.slice(0, 1).map((p, k) => (
                <p key={k} className="m-0 text-[15px] leading-relaxed text-foreground line-clamp-2">{p}</p>
              ))}
            </div>
            <button type="button" onClick={demarrer} className={`${gros} bg-naia-green text-card`}>
              Je commence
            </button>
          </section>
        ) : etape === 'lire' || etape === 'arreter' ? (
          <>
            <section className={`${carte} text-center`}>
              <p className="m-0 text-[44px] font-extrabold tabular-nums leading-none text-foreground">{mmss(secondes)}</p>
              <p className="m-0 mt-1 text-[13px] text-muted-foreground">
                {entrainement
                  ? 'Prends ton temps'
                  : etape === 'lire'
                    ? `${erreurs.size} mot${erreurs.size > 1 ? 's' : ''} touché${erreurs.size > 1 ? 's' : ''}`
                    : 'Touche le dernier mot lu'}
              </p>
            </section>

            <section className={carte}>
              {blocs.map((bloc, b) => (
              <p key={b} className="mt-0 mb-4 last:mb-0 text-[19px] leading-[2] text-foreground">
                {bloc.map((m) => {
                  const faux = erreurs.has(m.i);
                  const dernier = dernierMot === m.i;
                  return (
                    <span
                      key={m.i}
                      onClick={() => { if (!entrainement) toucherMot(m.i); }}
                      className={`inline-block px-[3px] rounded-sm cursor-pointer ${
                        dernier ? 'bg-naia-green text-card font-extrabold'
                          : faux ? 'bg-naia-orange-bg text-naia-orange font-bold underline decoration-2' : ''
                      }`}
                    >
                      {m.w}{' '}
                    </span>
                  );
                })}
              </p>
              ))}
            </section>

            <div className="flex flex-col gap-2">
              {etape === 'lire' ? (
                <button
                  type="button"
                  onClick={() => { if (entrainement) { arreter(); setEtape('repondre'); } else arreter(); }}
                  className={`${gros} bg-naia-blue text-card`}
                >
                  {entrainement ? "J'ai fini de lire" : "Stop — c'est fini"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => { setDernierMot(null); setEtape('verifier'); }}
                    className={`${gros} bg-naia-green text-card`}
                  >
                    Tout le texte a été lu
                  </button>
                  {dernierMot !== null ? (
                    <button type="button" onClick={() => setEtape('verifier')} className={`${gros} bg-naia-green text-card`}>
                      Valider — {dernierMot + 1} mot{dernierMot > 0 ? 's' : ''} lu{dernierMot > 0 ? 's' : ''}
                    </button>
                  ) : null}
                  <button type="button" onClick={reprendre} className={`${gros} bg-card text-foreground border border-border`}>
                    Reprendre la lecture
                  </button>
                </>
              )}
            </div>
          </>
        ) : etape === 'repondre' ? (
          questions.length === 0 ? (
            <section className={carte}>
              <p className="m-0 mb-4 text-[16px] text-foreground">Ce texte n&apos;a pas de question.</p>
              <button type="button" onClick={() => setEtape('verifier')} className={`${gros} bg-naia-green text-card`}>
                Voir le relevé
              </button>
            </section>
          ) : (
            <section className={carte}>
              {/* Une question à la fois, en grand : afficher les huit d'un coup
                  était illisible pour un enfant de sept ans. */}
              <p className="m-0 mb-1 text-[13px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                Question {qIndex + 1} sur {questions.length}
              </p>
              <p className="m-0 mb-4 text-[20px] font-extrabold leading-snug text-foreground">
                {questions[qIndex]!.enonce}
              </p>

              {questions[qIndex]!.type === 'qcm' && questions[qIndex]!.options ? (
                <div className="flex flex-col gap-2 mb-4">
                  {questions[qIndex]!.options!.map((opt, k) => {
                    const choisi = reponses[questions[qIndex]!.id] === String(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setReponses((r) => ({ ...r, [questions[qIndex]!.id]: String(k) }))}
                        className={`w-full min-h-[56px] px-5 py-4 rounded-sm text-left text-[17px] font-bold cursor-pointer border ${
                          choisi ? 'bg-naia-info-bg border-naia-blue text-foreground' : 'bg-card border-border text-foreground'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <textarea
                  value={reponses[questions[qIndex]!.id] ?? ''}
                  onChange={(e) => setReponses((r) => ({ ...r, [questions[qIndex]!.id]: e.target.value }))}
                  rows={3}
                  placeholder="Écris ta réponse…"
                  className="w-full mb-4 rounded-sm border border-border bg-card p-3 text-[17px] text-foreground"
                />
              )}

              {/* LA NOTATION, APRES CHAQUE QUESTION. On valide la réponse,
                  PUIS on la note — deux temps, comme demandé. Les codes sont
                  ceux du BO et se persistent tels quels : « 1 » réussi,
                  « A » réussi avec de l'aide, « 9 » pas réussi.
                  Le serveur ne corrige plus rien : c'est l'adulte qui a
                  écouté l'enfant qui juge, y compris les questions ouvertes —
                  1 279 des 2 144 questions du corpus, que personne ne notait. */}
              {noterQuestion && !entrainement ? (
                <div className="flex flex-col gap-2">
                  <p className="m-0 mb-1 text-[15px] font-extrabold text-foreground">
                    Comment a répondu {prenom || 'l’élève'} ?
                  </p>
                  {([
                    ['1', 'Réussi', 'bg-naia-green text-card'],
                    ['A', 'Réussi avec de l’aide', 'bg-naia-blue text-card'],
                    ['9', 'Pas réussi', 'bg-naia-orange text-card'],
                  ] as const).map(([code, libelle, cls]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => {
                        const scoresFinaux = { ...scores, [questions[qIndex]!.id]: code };
                        setScores(scoresFinaux);
                        setNoterQuestion(false);
                        if (qIndex < questions.length - 1) setQIndex((i) => i + 1);
                        else void envoyerComprehension(scoresFinaux);
                      }}
                      disabled={envoiEnCours}
                      className={`${gros} ${cls} disabled:opacity-60`}
                    >
                      {libelle}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNoterQuestion(false)}
                    className={`${gros} bg-card text-foreground border border-border`}
                  >
                    Revenir à la réponse
                  </button>
                </div>
              ) : (
              <div className="flex gap-2">
                {qIndex > 0 ? (
                  <button type="button" onClick={() => { setNoterQuestion(false); setQIndex((i) => i - 1); }} className={`${gros} bg-card text-foreground border border-border`}>
                    Précédent
                  </button>
                ) : null}
                {entrainement ? (
                  qIndex < questions.length - 1 ? (
                    <button type="button" onClick={() => setQIndex((i) => i + 1)} className={`${gros} bg-naia-blue text-card`}>
                      Suivant
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void envoyer()}
                      disabled={envoiEnCours}
                      className={`${gros} bg-naia-green text-card disabled:opacity-60`}
                    >
                      {envoiEnCours ? 'Enregistrement…' : 'Voir mes réponses'}
                    </button>
                  )
                ) : (
                  <button type="button" onClick={() => setNoterQuestion(true)} className={`${gros} bg-naia-blue text-card`}>
                    Valider la réponse
                  </button>
                )}
              </div>
              )}

              {/* Jamais de correction ici : elle se fait sur le serveur, et
                  l'enfant ne doit pas apprendre que la bonne réponse s'obtient
                  en regardant l'écran. */}
              <p className="m-0 mt-4 text-[13px] text-muted-foreground">
                Tu peux revenir en arrière tant que tu n&apos;as pas fini. Aucune réponse n&apos;est perdue.
              </p>
            </section>
          )
        ) : etape === 'lecture-enregistree' ? (
          <section className={carte}>
            <p className="m-0 text-[22px] font-extrabold text-foreground mb-2">La lecture est enregistrée.</p>
            {resultat ? (
              <div className="rounded-sm bg-naia-info-bg px-4 py-4 mb-4">
                <p className="m-0 text-[40px] font-extrabold leading-none text-foreground tabular-nums">{resultat.mclm}</p>
                <p className="m-0 mt-1 text-[13px] text-muted-foreground">
                  mots correctement lus par minute{secondes < 60 ? ` · extrapolé depuis ${secondes} s` : ''}
                </p>
                <p className="m-0 mt-3 text-[15px] font-bold text-foreground">
                  {resultat.position === null ? sansRepere
                    : resultat.position === 'sous' ? 'Sous le repère du niveau'
                    : resultat.position === 'dans' ? 'Dans le repère du niveau'
                    : 'Au-delà du repère du niveau'}
                  {resultat.seuilMin === null ? null : (
                    <span className="font-normal text-muted-foreground"> ({resultat.seuilMin}–{resultat.seuilMax})</span>
                  )}
                </p>
              </div>
            ) : null}
            {/* Les questions sont un SECOND temps, facultatif. S'arrêter ici ne
                coûte plus rien : la mesure est déjà en base. */}
            {questions.length > 0 ? (
              <>
                <p className="m-0 mb-4 text-[15px] text-muted-foreground leading-relaxed">
                  Vous pouvez passer aux {questions.length} questions de compréhension, ou vous arrêter
                  là — la mesure ne bougera plus.
                </p>
                <button type="button" onClick={() => setEtape('repondre')} className={`${gros} bg-naia-blue text-card`}>
                  Passer aux questions
                </button>
              </>
            ) : (
              <p className="m-0 text-[15px] text-muted-foreground">Ce texte n&apos;a pas de question.</p>
            )}
          </section>
        ) : etape === 'verifier' ? (
          <section className={carte}>
            {/* RIEN ne s'enregistre tout seul. L'enseignant relit le releve et
                decide : une mesure fausse ecrite d'office serait plus couteuse a
                corriger qu'une lecture refaite. */}
            <h2 className="m-0 mb-1 text-[20px] font-extrabold text-foreground">Le relev&eacute;</h2>
            <p className="m-0 mb-4 text-[14px] text-muted-foreground leading-relaxed">
              Relisez avant d&apos;enregistrer. Rien n&apos;est encore &eacute;crit.
            </p>
            {/* Le prototype le disait, nous l'avions perdu : sous une minute, le
                nombre de mots par minute est EXTRAPOLÉ. Plus la lecture est
                courte, plus il est fragile — 15 secondes multiplient l'écart par
                quatre. Un chiffre extrapolé qui ne le dit pas se lit comme une
                mesure. */}
            {secondes < 60 ? (
              <p className="m-0 mb-4 rounded-sm bg-naia-orange-bg px-4 py-3 text-[13.5px] text-foreground leading-relaxed">
                Lecture de <b>{secondes} secondes</b> : le nombre de mots par minute sera
                <b> extrapolé</b> (×{(60 / Math.max(1, secondes)).toFixed(1)}). Plus la lecture est courte,
                moins le chiffre est solide — vous pouvez recommencer et laisser lire plus longtemps.
              </p>
            ) : null}
            <dl className="m-0 mb-5 grid grid-cols-3 gap-3">
              {[
                ['Mots lus', String(motsLus)],
                ['Erreurs', String([...erreurs].filter((i) => i < motsLus).length)],
                ['Dur\u00e9e', mmss(secondes)],
              ].map(([k, v]) => (
                <div key={k} className="rounded-sm bg-naia-info-bg px-3 py-3">
                  <dt className="m-0 text-[12px] font-bold uppercase tracking-[0.04em] text-muted-foreground">{k}</dt>
                  <dd className="m-0 mt-1 text-[26px] font-extrabold tabular-nums text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
            {/* ARRETE AVANT D'ECRIRE, pas apres. Le serveur refuse une lecture
                de moins de cinq secondes — le MCLM ramene a la minute, et un
                appui trop rapide multiplie la mesure par trente. Mais decouvrir
                le refus APRES coup ferait perdre la seance : on le dit ici, et
                « Recommencer » reste ouvert. */}
            {!entrainement && secondes < DUREE_MINIMALE_SECONDES ? (
              <p className="m-0 mb-2 rounded-sm bg-naia-orange-bg px-4 py-3 text-sm text-foreground">
                Lecture de {secondes} s : trop courte pour être une mesure. Reprenez la lecture,
                ou recommencez.
              </p>
            ) : null}
            {/* Le MCLM n'est PAS affiche ici : il se calcule sur le serveur, et
                l'annoncer avant l'enregistrement laisserait croire qu'il est deja
                acquis. */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { if (entrainement) setEtape('repondre'); else void envoyer(); }}
                disabled={envoiEnCours || (!entrainement && secondes < DUREE_MINIMALE_SECONDES)}
                className={`${gros} bg-naia-green text-card disabled:opacity-60`}
              >
                {envoiEnCours ? 'Enregistrement\u2026' : entrainement ? 'Passer aux questions' : 'Enregistrer la lecture'}
              </button>
              {/* DÉSACTIVÉ PENDANT L'ENVOI. Le toucher alors qu'un
                  enregistrement est en vol abandonne l'écran sans annuler la
                  requête : la mesure abandonnée s'écrit quand même, et la
                  seconde — celle qu'on voulait vraiment — se heurte ensuite à
                  l'unicité. Les deux boutons sont voisins, et « Recommencer »
                  est exactement le geste de qui réalise en cours d'envoi qu'il
                  s'est trompé. */}
              <button
                type="button"
                onClick={recommencer}
                disabled={envoiEnCours}
                className={`${gros} bg-card text-foreground border border-border disabled:opacity-60`}
              >
                Recommencer
              </button>
            </div>
          </section>
        ) : (
          <section className={carte}>
            <p className="m-0 text-[22px] font-extrabold text-foreground mb-2">
              {entrainement ? 'Bien joué.' : "C'est enregistré."}
            </p>
            {entrainement ? (
              <>
                <p className="m-0 text-[15px] text-muted-foreground mb-4 leading-relaxed">
                  Tu as lu <b className="text-foreground">{support?.motsComptes ?? 0} mots</b> en {mmss(secondes)}.
                  Rien n&apos;a été enregistré : c&apos;était pour s&apos;entraîner.
                </p>
                <ul className="list-none m-0 p-0 flex flex-col gap-2">
                  {questions.map((q, k) => {
                    const v = verdicts.find((x) => x.questionId === q.id);
                    return (
                      <li key={q.id} className="flex items-start gap-3 rounded-sm border border-border px-4 py-3">
                        <span className="font-extrabold text-[15px] text-muted-foreground shrink-0">{k + 1}</span>
                        <span className="text-[15px] text-foreground min-w-0">{q.enonce}</span>
                        {/* Une question ouverte n'obtient pas de croix rouge :
                            elle se relit avec un adulte, elle ne se note pas. */}
                        <span className={`ml-auto shrink-0 text-[12px] font-extrabold uppercase tracking-[0.04em] px-2 py-[3px] rounded-full ${
                          !v || !v.corrigee ? 'bg-naia-info-bg text-naia-blue'
                            : v.correcte ? 'bg-naia-green-bg text-naia-green-deep'
                            : 'bg-naia-orange-bg text-naia-orange'
                        }`}>
                          {!v || !v.corrigee ? 'à relire ensemble' : v.correcte ? 'juste' : 'à revoir'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setSupport(null); setChoixId(''); setVerdicts([]); setReponses({});
                    setQIndex(0); setSecondes(0); setErreurs(new Set()); setDernierMot(null);
                    setEtape('choisir');
                  }}
                  className={`${gros} bg-naia-blue text-card mt-4`}
                >
                  Lire un autre texte
                </button>
              </>
            ) : null}
            {!entrainement && resultat ? (
              <>
                <p className="m-0 text-[15px] text-muted-foreground mb-4 leading-relaxed">
                  Lecture de <b className="text-foreground">{motsLus} mots</b> en {mmss(secondes)}.
                </p>
                <div className="rounded-sm bg-naia-info-bg px-4 py-4">
                  <p className="m-0 text-[40px] font-extrabold leading-none text-foreground tabular-nums">
                    {resultat.mclm}
                  </p>
                  <p className="m-0 mt-1 text-[13px] text-muted-foreground">
                    mots correctement lus par minute{secondes < 60 ? ` · extrapolé depuis ${secondes} s` : ''}
                  </p>
                  {/* Le repère est INDICATIF, et la référence figée avec le
                      résultat le dit. La taire revenait à le présenter comme
                      officiel. */}
                  {resultat.referenceSeuil ? (
                    <p className="m-0 mt-2 text-[12px] text-muted-foreground leading-snug">{resultat.referenceSeuil}</p>
                  ) : null}
                  <p className="m-0 mt-3 text-[15px] font-bold text-foreground">
                    {resultat.position === null ? sansRepere
                      : resultat.position === 'sous' ? 'Sous le repère du niveau'
                      : resultat.position === 'dans' ? 'Dans le repère du niveau'
                      : 'Au-delà du repère du niveau'}
                    {resultat.seuilMin === null ? (
                      <span className="font-normal text-muted-foreground"> — la mesure se compare à la lecture précédente</span>
                    ) : (
                      <span className="font-normal text-muted-foreground"> ({resultat.seuilMin}–{resultat.seuilMax})</span>
                    )}
                  </p>
                </div>
              </>
            ) : null}

            {/* LE DETAIL PAR CODE, et pas un ratio. « A » n'est pas une
                demi-reussite : c'est une observation distincte, et l'ecraser
                dans un « 4 sur 7 » perdrait exactement ce que le BO cherche a
                conserver. Les trois nombres se lisent ensemble. */}
            {!entrainement && Object.keys(scores).length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {([
                  ['1', 'réussie', 'réussies', 'bg-naia-green-bg text-naia-green-deep'],
                  ['A', 'avec aide', 'avec aide', 'bg-naia-info-bg text-naia-blue'],
                  ['9', 'non réussie', 'non réussies', 'bg-naia-orange-bg text-naia-orange'],
                ] as const).map(([code, singulier, pluriel, cls]) => {
                  const n = Object.values(scores).filter((x) => x === code).length;
                  return (
                    <span key={code} className={`rounded-sm px-3 py-2 text-[14px] font-extrabold ${cls}`}>
                      {n} {n > 1 ? pluriel : singulier}
                    </span>
                  );
                })}
              </div>
            ) : null}

            <p className="m-0 mt-4 text-[13px] text-muted-foreground leading-relaxed">
              Ce chiffre mesure une lecture, un jour donné. Il ne dit pas ce que tu vaux, et il bouge
              quand on s&apos;entraîne.
            </p>
          </section>
        )}
      </main>
    </>
  );
}
